import LegalPage, { Section, P, UL, Highlight, Placeholder } from "../components/LegalPage";

export default function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="October 2026" badge="UK GDPR">

      <Highlight>
        This Privacy Policy explains how Who's On? collects, uses, and protects your personal data
        in accordance with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
      </Highlight>

      <Section title="1. Who We Are">
        <P>Who's On? is a <strong className="text-white">non-commercial hobby project</strong> operated by an individual sole trader based in Warrington, Cheshire, England, United Kingdom. The service is provided free of charge and is not operated for profit.</P>
        <P>We are the data controller for personal data processed through the Who's On? platform. For data protection enquiries, contact: <a href="mailto:bbz199.bb@gmail.com" className="text-purple-400 hover:text-purple-300 underline">bbz199.bb@gmail.com</a></P>
        <P>As a non-commercial, free-to-use personal project, ICO registration requirements are being assessed. We are committed to following UK GDPR principles in full regardless of formal registration status, and will complete registration if required.</P>
      </Section>

      <Section title="2. Data We Collect">
        <P>We collect the following categories of personal data:</P>
        <p className="text-slate-400 text-sm font-semibold mt-2 mb-1">Account data</p>
        <UL items={[
          "Email address (used for login and account management).",
          "Password (stored as a securely hashed value — we never store your plain-text password).",
          "If you sign in via Google: your name and Google profile email (we do not store your Google profile photo).",
        ]} />
        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1">Profile data</p>
        <UL items={[
          "Your chosen alias (display name).",
          "Your selected avatar (a character type — not a personal photograph).",
          "Your account creation date.",
        ]} />
        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1">Activity data</p>
        <UL items={[
          "Chat messages sent during an evening session (deleted automatically at 11pm each night — see Section 5).",
          "Friends Room configuration, membership, and messages (retained until you delete the room).",
          "Friend connections (retained until you remove them).",
          "Reports submitted to our moderation system.",
          "Moderation actions taken against your account, where applicable.",
        ]} />
        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1">Technical data</p>
        <UL items={[
          "IP address (used for security and brute-force protection).",
          "Login timestamps.",
          "Device and browser information collected via standard server logs.",
        ]} />
      </Section>

      <Section title="3. How We Use Your Data">
        <P>We process your personal data on the following legal bases under UK GDPR:</P>
        <p className="text-slate-400 text-sm font-semibold mt-2 mb-1">Contractual necessity (Art. 6(1)(b))</p>
        <UL items={[
          "Creating and managing your account.",
          "Enabling you to use the chatroom service.",
          "Processing login and authentication.",
        ]} />
        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1">Legitimate interests (Art. 6(1)(f))</p>
        <UL items={[
          "Protecting the platform from abuse, spam, and harmful content.",
          "Brute-force login protection using IP-based attempt tracking.",
          "Operating the moderation and reporting system.",
          "Maintaining platform security and integrity.",
        ]} />
        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1">Legal obligation (Art. 6(1)(c))</p>
        <UL items={[
          "Retaining records necessary to comply with applicable law.",
          "Cooperating with law enforcement or regulatory bodies as required.",
        ]} />
        <p className="text-slate-400 text-sm font-semibold mt-3 mb-1">Consent (Art. 6(1)(a))</p>
        <UL items={[
          "Where you have provided explicit consent, such as accepting our Terms upon registration (including the 18+ confirmation).",
        ]} />
      </Section>

      <Section title="4. Who We Share Data With">
        <P>We do not sell your personal data. We do not share your personal data with third-party advertisers or data brokers.</P>
        <P>We may share data in the following limited circumstances:</P>
        <UL items={[
          "Google LLC: if you use Google Sign-In, Google processes your authentication. Their privacy policy applies to that interaction.",
          "Infrastructure providers: our hosting and database providers process data on our behalf under data processing agreements.",
          "Law enforcement: we will disclose data where required by law, court order, or to protect the safety of users or third parties.",
        ]} />
      </Section>

      <Section title="5. How Long We Keep Your Data">
        <P>We apply the following retention periods:</P>
        <UL items={[
          "Public chat messages: deleted automatically at the end of each evening session (11pm UK time). These are not retained after deletion.",
          "Account data (email, alias, avatar): retained for as long as your account remains active, plus a reasonable period thereafter for security and legal purposes.",
          "Friends Room messages: retained until the room creator deletes the room or you request erasure.",
          "Moderation records: retained for a period necessary to enforce our Terms and protect the platform.",
          "IP-based login attempt records: retained for a short period for security purposes only.",
          "Server logs: retained in accordance with standard infrastructure retention policies (typically 30 days).",
        ]} />
        <P>If you delete your account, we will delete or anonymise your personal data within 30 days, except where we are required by law to retain it.</P>
      </Section>

      <Section title="6. Your Rights Under UK GDPR">
        <P>You have the following rights regarding your personal data:</P>
        <UL items={[
          "Right of access: you may request a copy of the personal data we hold about you.",
          "Right to rectification: you may ask us to correct inaccurate data.",
          "Right to erasure (\u201cright to be forgotten\u201d): you may request that we delete your personal data, subject to legal obligations.",
          "Right to restriction: you may ask us to restrict processing in certain circumstances.",
          "Right to data portability: you may request your data in a commonly used, machine-readable format.",
          "Right to object: you may object to processing based on legitimate interests.",
          "Rights related to automated decision-making: we do not make solely automated decisions that produce significant legal effects about you.",
        ]} />
        <P>To exercise any of these rights, contact us at: <Placeholder label="privacy@yourdomain.com" />. We will respond within 30 days. We may need to verify your identity before processing your request.</P>
      </Section>

      <Section title="7. Data Security">
        <P>We implement appropriate technical and organisational measures to protect your personal data, including:</P>
        <UL items={[
          "Passwords are hashed using bcrypt — we never store them in plain text.",
          "All data is transmitted over HTTPS/TLS encrypted connections.",
          "Access to administrative systems is restricted to authorised personnel.",
          "Brute-force login protections are in place.",
          "Session tokens are stored in HttpOnly cookies to prevent JavaScript access.",
        ]} />
        <P>No system is completely secure. In the event of a data breach that is likely to result in a risk to your rights and freedoms, we will notify the ICO within 72 hours and affected users without undue delay, as required by UK GDPR.</P>
      </Section>

      <Section title="8. Cookies & Local Storage">
        <P>Who's On? uses the following cookies and browser storage:</P>
        <UL items={[
          "access_token (HttpOnly cookie): your login session token, expires in 60 minutes.",
          "refresh_token (HttpOnly cookie): allows session renewal, expires in 7 days.",
          "These are strictly necessary cookies required for the service to function.",
        ]} />
        <P>We do not use tracking cookies, advertising cookies, or any form of cross-site tracking. We do not use Google Analytics or similar third-party analytics services.</P>
      </Section>

      <Section title="9. International Data Transfers">
        <P>Your data is processed and stored within the United Kingdom or the European Economic Area. If any data processing occurs outside this area (for example, via Google's authentication infrastructure), we ensure that adequate safeguards are in place in accordance with UK GDPR requirements.</P>
      </Section>

      <Section title="10. Children's Privacy">
        <P>Who's On? is strictly for adults aged 18 and over. We do not knowingly collect personal data from anyone under 18. If we become aware that a user is under 18, we will delete their account and all associated data immediately. If you believe a minor has registered, please contact us at <Placeholder label="privacy@yourdomain.com" />.</P>
      </Section>

      <Section title="11. Changes to This Policy">
        <P>We may update this Privacy Policy from time to time. We will notify users of material changes via the platform. The date at the top of this page reflects when the policy was last updated. Continued use of the Service after updates constitutes acceptance of the revised Policy.</P>
      </Section>

      <Section title="12. Contact & Complaints">
        <P>For any privacy-related queries or to exercise your rights, contact:</P>
        <P>
          <strong className="text-white">WHO'S ON?</strong><br />
          Non-commercial hobby project · Warrington, Cheshire, England<br />
          Email: <a href="mailto:bbz199.bb@gmail.com" className="text-purple-400 hover:text-purple-300 underline">bbz199.bb@gmail.com</a>
        </P>
        <P>If you are not satisfied with how we handle your request, you have the right to lodge a complaint with the UK Information Commissioner's Office (ICO):</P>
        <P>
          <strong className="text-white">Information Commissioner's Office</strong><br />
          Wycliffe House, Water Lane, Wilmslow, Cheshire SK9 5AF<br />
          Website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline">ico.org.uk</a><br />
          Helpline: 0303 123 1113
        </P>
      </Section>

    </LegalPage>
  );
}
